"use client";

import { createContext, useCallback, useContext, useEffect, useMemo, useRef } from "react";
import type { PageContextProvider, PageSnapshot } from "@/lib/page-context";

/**
 * Per-route page-context registry. Pages register a snapshot function
 * keyed by their pathname; sendChat resolves the snapshot at message-send
 * time so the agent gets grounded data instead of just a label.
 *
 * Spec: docs/superpowers/specs/2026-05-03-page-context-l2-design.md
 */

interface PageContextRegistryValue {
  register: (path: string, fn: PageContextProvider) => () => void;
  resolve: (path: string) => PageSnapshot | null;
}

const PageContextRegistry = createContext<PageContextRegistryValue | null>(null);

/**
 * Wrap the workspace tree with this so any descendant page can register
 * a context provider. Registry storage is a single ref-backed map; we
 * never re-render the tree when a provider registers (snapshot fns are
 * called by `resolve`, not by render).
 */
export function PageContextRegistryProvider({ children }: { children: React.ReactNode }) {
  const providersRef = useRef(new Map<string, PageContextProvider>());

  const register = useCallback((path: string, fn: PageContextProvider) => {
    providersRef.current.set(path, fn);
    return () => {
      // Only delete if it's still the same fn — guards against a route's
      // unmount cleanup running AFTER the next route's mount has registered.
      if (providersRef.current.get(path) === fn) {
        providersRef.current.delete(path);
      }
    };
  }, []);

  const resolve = useCallback((path: string): PageSnapshot | null => {
    const fn = providersRef.current.get(path);
    if (!fn) return null;
    try {
      return fn();
    } catch (err) {
      // A buggy provider should never block chat send. Fall back to L1.
      console.warn("[page-context] provider for", path, "threw:", err);
      return null;
    }
  }, []);

  const value = useMemo(() => ({ register, resolve }), [register, resolve]);

  return (
    <PageContextRegistry.Provider value={value}>{children}</PageContextRegistry.Provider>
  );
}

/**
 * Subscribe a page's snapshot fn while the page is mounted. The fn
 * captures whatever state you reference; React re-creates it on every
 * render but the registry only stores the latest one (closes over
 * fresh state automatically).
 */
export function useRegisterPageContext(path: string, fn: PageContextProvider): void {
  const ctx = useContext(PageContextRegistry);
  // Keep the latest fn in a ref so the registry call only fires once
  // per path/mount, not on every render — but we still want fresh state.
  const fnRef = useRef(fn);
  fnRef.current = fn;

  useEffect(() => {
    if (!ctx) return;
    return ctx.register(path, () => fnRef.current());
  }, [ctx, path]);
}

/**
 * Read the registry from non-page code (chat-context's sendChat needs
 * this). Returns null outside the provider.
 */
export function usePageContextRegistry(): PageContextRegistryValue | null {
  return useContext(PageContextRegistry);
}
